import {
  ComponentPropsWithoutRef,
  isValidElement,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FieldProps, InputProps, useField } from 'informed';
import { InputWrapper, InputWrapperProps } from '../common';
import classNames from 'classnames';
import { FavouriteIcon } from '@tymbe/ui-icons';
import { Collapse } from '@tymbe/ui-primitives/*';
import { TySelectContext } from './context';

interface TySelectProps extends Omit<InputProps, 'children'>, InputWrapperProps {
  children?: ReactNode;
}

interface TyOptionProps extends ComponentPropsWithoutRef<'div'> {
  value: any;
  label: string;
}

export const TyOption = ({ value, onClick, onKeyUp, className, ...rest }: TyOptionProps) => {
  const context = useContext(TySelectContext);

  if (!context) {
    throw new Error('TyOption must be used inside TySelect');
  }

  const { tySelectApi } = context;

  return (
    <div
      tabIndex={0}
      className={classNames(
        'cursor-pointer',
        className,
      )}
      onClick={(e) => {
        tySelectApi.setValue(value);
        tySelectApi.setTouched(true);
        onClick?.(e);
        tySelectApi.setFocused(false);
      }}
      onKeyUp={(e) => {
        if (e.key === 'Enter') {
          tySelectApi.setValue(value);
          tySelectApi.setTouched(true);
          onKeyUp?.(e);
          tySelectApi.setFocused(false);
        }
      }}
      {...rest}
    />
  );
};

export const TySelectInput = <T = unknown>(props: FieldProps<TySelectProps, T, object>) => {
  const { render, userProps, ref, fieldState, fieldApi } = useField(props);
  const { setTouched, setFocused } = fieldApi;
  const { maskedValue, showError, error, focused } = fieldState;
  const { label, id, className, simple, icon, suffix, description, hidden, children, ...rest } = userProps;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const options = useMemo(
    () => [children].flat(2)
      .filter((option): option is ReactElement<TyOptionProps> => {
        return isValidElement(option) && option.type === TyOption
      }),
    [children],
  );

  const filteredOptions = useMemo(
    () => options
    .filter(option => option?.props.label.toLowerCase().includes(search.toLowerCase())),
    [options, search],
  );

  const selectedOptions = useMemo(
    () => options.filter(option => option?.props.value === maskedValue)[0],
    [options, maskedValue],
  );

  useEffect(() => {
    ref.current.setCustomValidity(error || '');
  }, [error, ref]);

  useEffect(() => {
    setOpen(focused);
  }, [focused]);

  return render(
    <div
      className="relative grow"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setTouched(true, e);
          setFocused(false, e);
        } else {
          setFocused(true, e);
        }
      }}
      onFocus={(e) => {
        if (e.currentTarget.contains(ref.current)) {
          setFocused(true, e);
        }
      }}
    >
      <InputWrapper
        className={classNames(className, 'relative z-10 *:relative')}
        simple={simple}
        icon={icon}
        suffix={suffix}
        label={label}
        error={showError ? String(error) : undefined}
        description={description}
        hidden={hidden}
        htmlFor={id}
      >
        <div className="relative grow">
          <input
            ref={ref}
            id={id}
            {...rest}
            className={classNames({ 'opacity-0': !focused })}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            onBlur={(e)=> {
              setTouched(true, e);
            }}
            onFocus={()=> {
              setSearch('');
            }}
          />
          <div
            className={classNames(
              'absolute left-0 right-0 top-0 bottom-0',
              { 'hidden': open && search },
            )}
          >
            {selectedOptions?.props.label}
          </div>
        </div>
        <FavouriteIcon
          name="chevron_down"
          className="size-5"
          onMouseDownCapture={(e) => {
            e.preventDefault();
            if (open) {
              ref.current.blur();
            } else {
              ref.current.focus();
            }
          }}
          onClick={(e) => { e.preventDefault(); }}
        />
      </InputWrapper>
      <Collapse className="absolute top-full left-0 right-0 z-0" open={open}>
        <TySelectContext.Provider value={{ open, setOpen, tySelectApi: fieldApi, tySelectState: fieldState }}>
          <div className="border border-secondary-400 rounded-lg bg-white">
            {filteredOptions}
          </div>
        </TySelectContext.Provider>
      </Collapse>
    </div>,
  );
};
